import React, { useEffect, useState } from 'react';
import { listTaskTemplates, createTaskTemplate, activateTaskTemplate, deactivateTaskTemplate, saveStageTaskTemplates } from '../api';

const stageOptions = [
  'Fact Finding & Strategy',
  'Action & Drafting',
  'Negotiation & Resolution',
  'Court/Formal Proceedings',
  'Post-Resolution/Closure'
];

const caseTypeOptions = [
  'Family','Criminal','Civil','Immigration','Litigation','Real_Estate','Corporate','Contract','Property','Intellectual_Property','Transactional','Advisory','Arbitration','Mediation','Other'
];

export default function TaskTemplatesManager({ authAxios, permissions, showFormMessage }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ case_type: 'Litigation', case_stage: 'Fact Finding & Strategy' });
  const [form, setForm] = useState({ title: '', description: '', default_due_days_offset: 2 });
  const [createBusy, setCreateBusy] = useState(false);

  const canManage = permissions?.isPartner || permissions?.isAssociate || permissions?.isSuperuser;

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const data = await listTaskTemplates(authAxios, filters);
      setTemplates(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      showFormMessage && showFormMessage('Failed to load templates', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTemplates(); }, [filters.case_type, filters.case_stage]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title) return;
    setCreateBusy(true);
    try {
      await createTaskTemplate(authAxios, { ...form, case_type: filters.case_type, case_stage: filters.case_stage });
      setForm({ title: '', description: '', default_due_days_offset: 2 });
      fetchTemplates();
      showFormMessage && showFormMessage('Template created');
    } catch (err) {
      console.error(err);
      showFormMessage && showFormMessage('Failed to create template', 'error');
    } finally {
      setCreateBusy(false);
    }
  };

  const toggleActive = async (tpl) => {
    try {
      if (tpl.is_active) {
        await deactivateTaskTemplate(authAxios, tpl.id);
      } else {
        await activateTaskTemplate(authAxios, tpl.id);
      }
      fetchTemplates();
    } catch (err) {
      console.error(err);
      showFormMessage && showFormMessage('Update failed', 'error');
    }
  };

  const handleSaveFromCase = async () => {
    const caseId = prompt('Enter Case ID to save current stage tasks as templates (will overwrite).');
    if (!caseId) return;
    try {
      await saveStageTaskTemplates(authAxios, caseId, true);
      fetchTemplates();
      showFormMessage && showFormMessage('Templates saved from case');
    } catch (err) {
      console.error(err);
      showFormMessage && showFormMessage('Failed saving from case', 'error');
    }
  };

  return (
    <div className="p-4 space-y-4">
      <h2 className="text-xl font-semibold">Task Templates</h2>
      <div className="flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-sm font-medium">Case Type</label>
          <select className="border px-2 py-1" value={filters.case_type} onChange={e => setFilters(f => ({ ...f, case_type: e.target.value }))}>
            {caseTypeOptions.map(ct => <option key={ct} value={ct}>{ct}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium">Case Stage</label>
          <select className="border px-2 py-1" value={filters.case_stage} onChange={e => setFilters(f => ({ ...f, case_stage: e.target.value }))}>
            {stageOptions.map(st => <option key={st} value={st}>{st}</option>)}
          </select>
        </div>
        {canManage && (
          <button onClick={handleSaveFromCase} className="bg-indigo-600 text-white px-3 py-2 rounded shadow">Save From Case</button>
        )}
      </div>

      {canManage && (
        <form onSubmit={handleCreate} className="border rounded p-4 space-y-2 bg-white shadow">
          <h3 className="font-medium">Create Template</h3>
          <div>
            <label className="block text-xs uppercase">Title</label>
            <input className="border w-full px-2 py-1" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
          </div>
          <div>
            <label className="block text-xs uppercase">Description</label>
            <textarea className="border w-full px-2 py-1" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div>
            <label className="block text-xs uppercase">Due Offset (days)</label>
            <input type="number" min="0" className="border w-32 px-2 py-1" value={form.default_due_days_offset} onChange={e => setForm(f => ({ ...f, default_due_days_offset: parseInt(e.target.value)||0 }))} />
          </div>
          <button disabled={createBusy} className="bg-green-600 text-white px-3 py-1 rounded disabled:opacity-50">{createBusy ? 'Saving...' : 'Add Template'}</button>
        </form>
      )}

      <div className="mt-4">
        {loading ? <div>Loading templates...</div> : (
          <table className="min-w-full text-sm border">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-2 border">Title</th>
                <th className="p-2 border">Description</th>
                <th className="p-2 border">Due Offset</th>
                <th className="p-2 border">Active</th>
                {canManage && <th className="p-2 border">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {templates.map(t => (
                <tr key={t.id} className="hover:bg-gray-50">
                  <td className="p-2 border whitespace-pre-wrap">{t.title}</td>
                  <td className="p-2 border whitespace-pre-wrap max-w-xs">{t.description}</td>
                  <td className="p-2 border text-center">{t.default_due_days_offset ?? 2}</td>
                  <td className="p-2 border text-center">
                    {t.is_active ? <span className="text-green-700 font-medium">Yes</span> : <span className="text-red-600">No</span>}
                  </td>
                  {canManage && (
                    <td className="p-2 border text-center">
                      <button onClick={() => toggleActive(t)} className="text-sm px-2 py-1 rounded bg-gray-800 text-white">
                        {t.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {templates.length === 0 && !loading && (
                <tr><td colSpan={5} className="p-4 text-center text-gray-500">No templates found for selection.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
